// src/pages/admin/AdminPromoCodes.js — Création & gestion des codes promo
// Maxula
import React, { useEffect, useState, useCallback } from 'react';
import { FiTag, FiPlus, FiTrash2, FiCopy, FiShare2, FiRefreshCw, FiPower, FiEdit2, FiX, FiSave, FiMessageCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { promoAPI, chatAPI } from '../../services/api';

// ---- Carte coupon (design) ----
function CouponCard({ promo }) {
  const color = promo.color || '#e11d7b';
  const valueLabel = promo.type === 'percentage' ? `-${promo.value}%` : `-${promo.value} DH`;
  const expire = promo.expiresAt ? new Date(promo.expiresAt).toLocaleDateString('fr-FR') : null;
  return (
    <div className="relative overflow-hidden rounded-2xl text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}>
      <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/90" />
      <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/90" />
      <div className="p-5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider opacity-90">Code promo Maxula</span>
          <FiTag />
        </div>
        <div className="mt-3 flex items-end gap-2">
          <span className="text-4xl font-extrabold leading-none">{valueLabel}</span>
          <span className="opacity-90 text-sm mb-1">de réduction</span>
        </div>
        {promo.description ? <p className="mt-1 text-sm opacity-90">{promo.description}</p> : null}
        <div className="mt-4 border-2 border-dashed border-white/60 rounded-lg px-3 py-2 text-center font-mono font-bold tracking-widest text-lg">
          {promo.code || 'CODE'}
        </div>
        <div className="mt-3 flex items-center justify-between text-[11px] opacity-90">
          <span>{Number(promo.minPurchase) > 0 ? `Dès ${promo.minPurchase} DH d'achat` : 'Sans minimum'}</span>
          <span>{expire ? `Exp. ${expire}` : 'Sans expiration'}</span>
        </div>
      </div>
    </div>
  );
}

const EMPTY = {
  type: 'percentage', value: '', code: '', description: '',
  minPurchase: '', maxDiscount: '', expiresAt: '', usageLimit: '', perUserLimit: '', newCustomersOnly: false, color: '#e11d7b'
};

export default function AdminPromoCodes() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [chatShare, setChatShare] = useState(null); // promo en cours de partage via chat
  const [convs, setConvs] = useState([]);
  const [convsLoading, setConvsLoading] = useState(false);
  const [sendingTo, setSendingTo] = useState(null);
  const singleUse = String(form.usageLimit) === '1';

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await promoAPI.list(); setPromos(Array.isArray(data?.data) ? data.data : []); }
    catch (e) { setPromos([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Format une date ISO -> 'YYYY-MM-DD' (local) pour l'input type=date
  const toDateInput = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '';
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      type: p.type || 'percentage',
      value: p.value ?? '',
      code: p.code || '',
      description: p.description || '',
      minPurchase: p.minPurchase ?? '',
      maxDiscount: p.maxDiscount ?? '',
      expiresAt: toDateInput(p.expiresAt),
      usageLimit: p.usageLimit ?? '',
      perUserLimit: p.perUserLimit ?? '',
      newCustomersOnly: !!p.newCustomersOnly,
      color: p.color || '#e11d7b'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const submit = async () => {
    if (!form.value || Number(form.value) <= 0) { toast.error('Indiquez une valeur de réduction'); return; }
    if (form.type === 'percentage' && Number(form.value) > 100) { toast.error('Le pourcentage ne peut dépasser 100'); return; }
    setSaving(true);
    try {
      if (editingId) {
        // Le code lui-même n'est pas modifiable (déjà distribué) -> on ne l'envoie pas.
        const { code, ...patch } = form;
        await promoAPI.update(editingId, patch);
        toast.success('Code promo mis à jour ✅');
      } else {
        await promoAPI.create(form);
        toast.success('Code promo créé ✅');
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally { setSaving(false); }
  };

  const toggle = async (p) => {
    try { await promoAPI.update(p.id, { isActive: !p.isActive }); setPromos((prev) => prev.map((x) => x.id === p.id ? { ...x, isActive: !x.isActive } : x)); }
    catch (e) { toast.error('Erreur'); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Supprimer le code ${p.code} ?`)) return;
    try { await promoAPI.remove(p.id); setPromos((prev) => prev.filter((x) => x.id !== p.id)); }
    catch (e) { toast.error('Erreur'); }
  };
  const copy = (p) => { navigator.clipboard?.writeText(p.code); toast.success('Code copié'); };
  const share = (p) => {
    const val = p.type === 'percentage' ? `-${p.value}%` : `-${p.value} DH`;
    const exp = p.expiresAt ? ` (valable jusqu'au ${new Date(p.expiresAt).toLocaleDateString('fr-FR')})` : '';
    const text = `🎁 Votre code promo Maxula : ${p.code}\n${val} de réduction${p.minPurchase > 0 ? ` dès ${p.minPurchase} DH d'achat` : ''}${exp}.\nAppliquez-le dans votre panier sur Maxula.ma`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  // ---- Partage on-platform (chat Maxula) pour les codes MARCHAND ----
  const promoMessage = (p) => {
    const val = p.type === 'percentage' ? `-${p.value}%` : `-${p.value} DH`;
    const min = Number(p.minPurchase) > 0 ? ` dès ${p.minPurchase} DH d'achat` : '';
    const exp = p.expiresAt ? ` (valable jusqu'au ${new Date(p.expiresAt).toLocaleDateString('fr-FR')})` : '';
    return `Votre code promo : ${p.code} — ${val} de réduction${min}${exp}. Appliquez-le dans votre panier sur Maxula.`;
  };
  const custName = (c) => {
    const u = c.customerId;
    if (u && typeof u === 'object') return `${u.firstName || ''} ${u.lastName || ''}`.trim() || `Client #${u._id}`;
    return `Client #${u}`;
  };
  const openChatShare = async (p) => {
    setChatShare(p);
    setConvsLoading(true);
    try {
      const { data } = await chatAPI.list('merchant');
      setConvs(Array.isArray(data?.data) ? data.data : []);
    } catch (e) { setConvs([]); }
    finally { setConvsLoading(false); }
  };
  const closeChatShare = () => { setChatShare(null); setConvs([]); setSendingTo(null); };
  const sendToConv = async (conv) => {
    if (!chatShare) return;
    setSendingTo(conv.id);
    try {
      await chatAPI.send(conv.id, promoMessage(chatShare));
      toast.success(`Code envoyé à ${custName(conv)} dans le chat`);
      closeChatShare();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Échec de l\'envoi');
    } finally { setSendingTo(null); }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center gap-2 mb-1">
        <FiTag className="text-[#e11d7b]" size={24} />
        <h1 className="text-2xl font-bold text-gray-900">Codes Promo / Réductions</h1>
      </div>
      <p className="text-gray-500 mb-6">Créez des coupons de réduction (% ou montant), puis envoyez-les à vos clients. Ils les appliquent au panier.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulaire */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">{editingId ? 'Modifier le code promo' : 'Nouveau code promo'}</h2>
            {editingId && (
              <button onClick={cancelEdit} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                <FiX size={14} /> Annuler
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Type de réduction</label>
              <select value={form.type} onChange={(e) => set('type', e.target.value)} className="w-full border rounded-lg px-3 py-2">
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed">Montant fixe (DH)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{form.type === 'percentage' ? 'Pourcentage' : 'Montant (DH)'}</label>
              <input type="number" min="1" value={form.value} onChange={(e) => set('value', e.target.value)} placeholder={form.type === 'percentage' ? '20' : '100'} className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Code {editingId ? '(non modifiable)' : '(vide = auto)'}</label>
              <input value={form.code} onChange={(e) => set('code', e.target.value.toUpperCase())} disabled={!!editingId} placeholder="EX : RAMADAN20" className={`w-full border rounded-lg px-3 py-2 font-mono ${editingId ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Couleur du coupon</label>
              <input type="color" value={form.color} onChange={(e) => set('color', e.target.value)} className="w-full border rounded-lg h-[42px] px-1" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1">Description (optionnel)</label>
              <input value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="EX : Offre de bienvenue" className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Achat minimum (DH)</label>
              <input type="number" min="0" value={form.minPurchase} onChange={(e) => set('minPurchase', e.target.value)} placeholder="0" className="w-full border rounded-lg px-3 py-2" />
            </div>
            {form.type === 'percentage' && (
              <div>
                <label className="block text-sm font-medium mb-1">Remise max (DH, optionnel)</label>
                <input type="number" min="0" value={form.maxDiscount} onChange={(e) => set('maxDiscount', e.target.value)} placeholder="—" className="w-full border rounded-lg px-3 py-2" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Expiration (optionnel)</label>
              <input type="date" value={form.expiresAt} onChange={(e) => set('expiresAt', e.target.value)} className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nb max d'utilisations (optionnel)</label>
              <input type="number" min="1" value={form.usageLimit} onChange={(e) => set('usageLimit', e.target.value)} placeholder="illimité" disabled={singleUse} className={`w-full border rounded-lg px-3 py-2 ${singleUse ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`} />
              <label className="mt-2 flex items-center gap-2 cursor-pointer select-none text-sm">
                <input type="checkbox" checked={singleUse} onChange={(e) => set('usageLimit', e.target.checked ? '1' : '')} className="w-4 h-4 accent-[#e11d7b]" />
                <span>Code à usage unique <span className="text-xs text-gray-500">(expire après la 1ère utilisation)</span></span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max par client (optionnel)</label>
              <input type="number" min="1" value={form.perUserLimit} onChange={(e) => set('perUserLimit', e.target.value)} placeholder="illimité" className="w-full border rounded-lg px-3 py-2" />
            </div>
          </div>
          <label className="mt-4 flex items-start gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={!!form.newCustomersOnly} onChange={(e) => set('newCustomersOnly', e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#e11d7b]" />
            <span className="text-sm">
              <span className="font-medium">Réservé aux nouveaux clients</span>
              <span className="block text-xs text-gray-500">Utilisable uniquement par un client qui n'a encore aucune commande payée (offre de bienvenue / 1ère commande).</span>
            </span>
          </label>
          <button onClick={submit} disabled={saving} className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#e11d7b] text-white font-semibold hover:opacity-90 disabled:opacity-60">
            {editingId ? <FiSave /> : <FiPlus />} {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer les modifications' : 'Créer le code promo')}
          </button>
        </div>

        {/* Aperçu */}
        <div>
          <h2 className="font-semibold mb-4">Aperçu</h2>
          <CouponCard promo={{ ...form, value: form.value || 0 }} />
        </div>
      </div>

      {/* Liste */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Codes existants ({promos.length})</h2>
          <button onClick={load} className="p-2 border rounded-lg hover:bg-gray-50"><FiRefreshCw size={16} /></button>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#e11d7b]" /></div>
        ) : promos.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-500">Aucun code promo pour l'instant.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {promos.map((p) => (
              <div key={p.id} className="space-y-2">
                <CouponCard promo={p} />
                <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                  <span>{p.usageLimit != null ? `${p.usedCount || 0}/${p.usageLimit} utilisés` : `${p.usedCount || 0} utilisé(s)`}</span>
                  <span className="flex items-center gap-1">
                    {p.newCustomersOnly && <span className="px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">Nouveaux clients</span>}
                    <span className={`px-2 py-0.5 rounded-full font-medium ${p.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{p.isActive ? 'Actif' : 'Inactif'}</span>
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => copy(p)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border rounded-lg text-xs hover:bg-gray-50"><FiCopy size={13} /> Copier</button>
                  {p.scope === 'merchant' ? (
                    <button onClick={() => openChatShare(p)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-[#e11d7b] text-white rounded-lg text-xs hover:opacity-90" title="Partager via le chat Maxula"><FiMessageCircle size={13} /> Chat</button>
                  ) : (
                    <button onClick={() => share(p)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 text-white rounded-lg text-xs hover:bg-green-700"><FiShare2 size={13} /> WhatsApp</button>
                  )}
                  <button onClick={() => startEdit(p)} className="px-2 py-1.5 border rounded-lg text-xs hover:bg-gray-50" title="Modifier"><FiEdit2 size={13} /></button>
                  <button onClick={() => toggle(p)} className="px-2 py-1.5 border rounded-lg text-xs hover:bg-gray-50" title={p.isActive ? 'Désactiver' : 'Activer'}><FiPower size={13} /></button>
                  <button onClick={() => remove(p)} className="px-2 py-1.5 border rounded-lg text-xs text-red-600 hover:bg-red-50"><FiTrash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-center text-xs text-gray-400 mt-8">Maxula — contact@maxula.ma</p>

      {/* Modale : partager un code marchand via le chat Maxula */}
      {chatShare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={closeChatShare}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="font-semibold text-gray-900">Partager dans le chat</h3>
                <p className="text-xs text-gray-500">Code <span className="font-mono font-semibold">{chatShare.code}</span> — choisissez un client</p>
              </div>
              <button onClick={closeChatShare} className="p-1.5 rounded-lg hover:bg-gray-100"><FiX size={18} /></button>
            </div>
            <div className="p-2 overflow-y-auto">
              {convsLoading ? (
                <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e11d7b]" /></div>
              ) : convs.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">
                  Aucune conversation pour l'instant. Le client doit d'abord vous écrire (ou avoir une commande) pour recevoir le code dans le chat.
                </div>
              ) : (
                <ul className="divide-y">
                  {convs.map((c) => (
                    <li key={c.id}>
                      <button onClick={() => sendToConv(c)} disabled={sendingTo === c.id}
                        className="w-full flex items-center justify-between gap-3 px-3 py-3 hover:bg-gray-50 rounded-lg text-left disabled:opacity-60">
                        <div className="min-w-0">
                          <div className="font-medium text-sm text-gray-900 truncate">{custName(c)}</div>
                          {c.dealTitle ? <div className="text-xs text-gray-500 truncate">{c.dealTitle}</div> : null}
                        </div>
                        <span className="shrink-0 flex items-center gap-1 text-xs text-[#e11d7b] font-semibold">
                          <FiMessageCircle size={14} /> {sendingTo === c.id ? 'Envoi…' : 'Envoyer'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
